import React, { useEffect, useRef, useState } from 'react';
import { Button, IconButton } from 'global/components/Button';
import { AutoComplete } from 'global/components/Autocomplete';
import { first, flatten, get, set, size, unset } from 'global/lib/lodash';
import { Icon } from '@mui/material';
import { AUTOTAG } from '../index';
import { Text } from 'global/components/text';
import SnackAlerts from 'global/components/snack-alerts';
import { Column, Row } from 'global/components/row';
import clsx from 'clsx';
import { useWindowSize } from 'global/hooks/useWindowSize';
import { getRealmChildTier } from 'global/utils';
import Modal from 'global/components/modal';
import Form from 'global/components/form';
import sendData from 'redux/actions/sendData';
import { useUser } from 'global/hooks/useUser';
import SubScoreModal from 'global/components/SubScoreModal';
import RealmsCheckpointsModal from './RealmsCheckpointsModal';
import { ScorecardScoreIncludes } from 'global/hooks/useQueue';
import { isActionable, setFilters } from '../utils';
import { isDark } from 'global/styles';
import { RoomBtnWrapper, SUBS_ROOT_CLASS, SUBS_INPUT_CLASS } from './SubScoreLauncher.styles';
import { alpha, styled as styledMUI } from '@mui/material/styles';

export const buildTagFromCheckpoint = (cp) => {
  if (!cp) {
    return null;
  }

  const tag = {
    pssvSystem: {},
    pssvChecklist: {},
    pssvCheckpoint: {
      checkpoint: {},
    },
  };

  set(tag, 'pssvSystem', { id: cp.supersetId });
  set(tag, 'pssvChecklist', { ...cp.checklist, supersetId: cp.supersetId });
  set(tag, 'pssvCheckpoint', cp);

  return tag;
};

export const deleteSubScore = async (score) => {
  await sendData({
    url: `/sub-scores/${score.id}`,
    method: 'patch',
    data: {
      data: {
        id: score.id,
        type: 'sub-scores',
        attributes: {
          archived: 1,
        },
      },
    },
  });

  return getSubProcessedScore(score);
};

export const modifySubScore = async (score) => {
  await sendData({
    url: `/sub-scores/${score.id}`,
    method: 'patch',
    data: {
      data: {
        id: score.id,
        type: 'sub-scores',
        attributes: {
          ...score,
          id: undefined,
        },
      },
    },
  });

  return getSubProcessedScore(score);
};

export const getSubProcessedScore = async (score) => {
  const reqs = await Promise.all([
    sendData(
      {
        url: `/scorecard-scores/${score.scorecardScoreId}${ScorecardScoreIncludes}`,
        method: 'get',
      },
      { normalize: { build: ['scorecardScores'] } }
    ),
    get(score, 'checklist.id') &&
      sendData(
        {
          url: `/checklists/${get(score, 'checklist.id')}?include=tradeTypes`,
          method: 'get',
        },
        { normalize: { build: ['checklists'] } }
      ),
  ]);

  const scscore = first(reqs[0]);

  const checklist = first(reqs[1]);

  set(
    scscore,
    'processedScorecardScoreValue.subscores',
    get(scscore, 'processedScorecardScoreValue.subScores', []).map((ss) =>
      formatSubScore(ss, {
        ...get(scscore, 'processedScorecardScoreValue', {}),
        checklist,
      })
    )
  );
  set(scscore, 'processedScorecardScoreValue.filters.act', isActionable(get(scscore, 'processedScorecardScoreValue')));

  return [scscore];
};

export const deleteSubsOnCheckpoint = async (checkpoint) => {
  const checkscores = await sendData(
    {
      url: `/sub-scores`,
      method: 'get',
    },
    {
      normalize: { build: ['subScores'] },
      params: {
        filter: {
          scorecard_score_id: get(checkpoint, 'scorecardScoreId'),
          // assessment_id: get(checkpoint, 'assessmentId'),
          realm_id: get(checkpoint, 'realmId'),
        },
      },
    }
  );

  const autos = checkscores.filter((f) => get(f, 'notes') === AUTOTAG && f.id !== get(checkpoint, 'id'));
  return await Promise.all(autos.map(deleteSubScore));
};

export const createSubScore = async (data, options = {}) => {
  options = {
    removeAutos: true,
    ...options,
  };

  const hasId = get(data, 'data.id');
  const result = await sendData(
    {
      url: `/sub-scores/${hasId || ''}?include=images,realm,checklist,tradeTypes`,
      method: hasId ? 'patch' : 'post',
      data,
    },
    { normalize: { build: ['subScores'] } }
  );

  const deleted = !options.removeAutos ? [] : await deleteSubsOnCheckpoint(result[0]);

  const pscore = await getSubProcessedScore(first(result));

  return flatten([...pscore, ...deleted]);
};

export const formatSubScore = (score, parent = {}) => {
  const { filters, ...rest } = parent;
  const obj = {
    ...rest,
    ...score,
    // Keep the instance id even when the relationship object does not enumerate `id`.
    id: get(score, 'id', rest.id),
    checklist: { ...get(parent, 'checklist', get(parent, 'parent', {})) },
    subScores: undefined,
    // Parent checkpoints own the instance list; spreading it onto each instance
    // makes By System bulk-select treat a single instance as all of its siblings.
    subscores: undefined,
    value: get(score, 'value', score.score),
    images: (score.images || []).map((img) => ({
      ...img,
      thumb: img.thumbUrl,
      full: img.fullUrl,
      large: img.largeUrl,
    })),
    isSubScore: true,
    notes: score.notes,
    _subscore: score,
  };
  set(obj, 'filters', setFilters(obj));
  set(obj, '_subscore.notes', score.notes);
  unset(obj, 'parentId');
  unset(obj, 'parent');
  unset(obj, 'checklist.parentId');
  unset(obj, 'checklist.parent');
  unset(obj, 'checklist.children');
  return obj;
};

export const getDisplayConfigFrom = (scorecard, opts = {}, isNew = false) => {
  const { tag } = opts;
  const { hasSubScores, editScoreInfo, subScoringClass } = get(scorecard, 'config', {});
  const isLocation = get(opts, 'filter.listGroup') === 'location';

  const isView1 = isLocation ? false : hasSubScores && editScoreInfo && subScoringClass === null;
  const isView2 = isLocation ? true : hasSubScores && !editScoreInfo && subScoringClass != null;
  const isView3 = isLocation ? false : hasSubScores && editScoreInfo && subScoringClass != null;

  let name = `${get(tag, 'pssvChecklist.name', get(tag, 'pssvChecklist.checklist.name', ''))}: ${get(tag, 'pssvCheckpoint.name')}`;

  if (!isNew && !isLocation && (isView1 || isView3)) {
    name = get(tag, 'pssvCheckpoint.realm.name', '');
  }

  return {
    isView1,
    isView2,
    isView3,
    name,
    hasSubScores,
    editScoreInfo,
    subScoringClass,
    showObservationGrid: true,
  };
};

export const filterRealmsByCps = (realms, scorecard, props) => {
  const realmCps = get(scorecard, 'config.roomCheckpoints', []);
  const realmTier = get(realms, '0.tier', 'lot');
  const allsubs = [];
  get(props, 'allScoreData', []).map((s) => {
    (s.subscores || []).map((ss) => allsubs.push(ss));
  });

  if (!size(realmCps) || realmTier !== 'room') {
    return realms;
  }

  const ret = [];

  realmCps.map((r) => {
    const { cps, id, act } = r;
    if (act !== undefined && !act) {
      return;
    }

    if (size(cps) || !!act) {
      const realm = realms.find((r) => r.id == id);
      if (realm) {
        ret.push(realm);
      }
    }
  });

  allsubs.map((s) => {
    const { realmId } = s;

    if (!ret.find((r) => r.id == realmId)) {
      const realm = realms.find((r) => r.id == realmId);
      if (realm) {
        ret.push(realm);
      }
    }
  });

  return ret;
};

const BtnContainer = styledMUI('div')(({ theme }) => {
  const appBarBg = isDark(theme) ? theme.palette.secondary.dark : theme.palette.secondary.main;
  const appBarFg = theme.palette.getContrastText(appBarBg);
  return {
    cursor: 'pointer',
    width: 250,
    flexDirection: 'column',
    alignItems: 'flex-end',
    display: 'flex',
    '& input': {
      padding: `${theme.spacing(1)} !important`,
    },
    '&.on-app-bar': {
      '& .MuiInputBase-root, & .MuiInputBase-input': {
        color: `${appBarFg} !important`,
      },
      '& .MuiInputBase-input::placeholder': {
        color: appBarFg,
        opacity: 0.7,
      },
      '& .MuiInput-root::before': {
        borderBottomColor: `${alpha(appBarFg, 0.42)} !important`,
      },
      '& .MuiInput-root:hover:not(.Mui-disabled)::before': {
        borderBottomColor: `${appBarFg} !important`,
      },
      '& .MuiInput-root::after': {
        borderBottomColor: `${appBarFg} !important`,
      },
    },
  };
});
const BtnInner = styledMUI(Button)({
  width: 250,
  paddingRight: '2px',
  marginRight: '-2px',
});

const ButtonType = (props) => {
  const { variant: buttonVariant = 'contained', ...autoCompleteProps } = props;
  const [state, setState] = useState({
    open: false,
  });

  const [createRealm, setCreateRealm] = useState({
    open: false,
    onClose: () => {
      setCreateRealm((s) => ({ ...s, open: false }));
    },
  });

  const { isMobileXS, windowSize } = useWindowSize();
  const autoCompleteRef = useRef();

  const hasTitle = get(props, 'title');

  const tierChildLabel = getRealmChildTier(get(props, 'rootProps.assessment.data.realm.tier', 'lot'));
  const placeholder = get(props, 'placeholder', `ADD BY ${tierChildLabel}`).toUpperCase();

  useEffect(() => {
    const body = document.body;
    // Inline style toggle (was previously a JSS class). Avoid clobbering existing styles.
    if (state.open) {
      body.style.overflow = 'hidden';
      body.style.height = '100%';
    } else {
      body.style.overflow = '';
      body.style.height = '';
    }

    return () => {
      body.style.overflow = '';
      body.style.height = '';
    };
  }, [state]);

  const onOpen = () => {
    setState((s) => ({ ...s, open: true }));
  };

  const onClose = () => {
    setState((s) => ({ ...s, open: false }));
  };

  const onChange = (e, realm) => {
    setState((s) => ({ ...s, open: false }));

    if (realm && realm.id === 'addnew') {
      setCreateRealm((s) => ({
        ...s,
        title: `Create new ${tierChildLabel}`.toUpperCase(),
        open: true,
        onSubmit: (rm) => {
          props.onChange && props.onChange(e, rm);
          s.onClose();
        },
      }));
    } else {
      props.onChange && props.onChange(e, realm);
    }
  };

  const clickContainer = () => {
    setState((s) => ({ ...s, open: true }));
  };

  const getButton = () => {
    if (hasTitle) {
      return (
        <Row style={{ justifyContent: 'flex-end' }}>
          <Text variant={'overline'} style={{ marginRight: 8, lineHeight: 'inherit' }}>
            {placeholder}
          </Text>
          <Icon>arrow_drop_down</Icon>
        </Row>
      );
    }
    return (
      <BtnInner onClick={clickContainer} variant={buttonVariant}>
        <Row fullWidth spaceBetween>
          {placeholder}
          <Icon>arrow_drop_down</Icon>
        </Row>
      </BtnInner>
    );
  };

  return (
    <>
      <BtnContainer className={clsx(hasTitle && 'on-app-bar')} onClick={!state.open ? clickContainer : undefined}>
        {!!hasTitle && (
          <div style={{ width: windowSize.innerWidth - 90, textAlign: 'right' }}>
            <Text
              style={{
                fontSize: state.open ? (isMobileXS ? 11 : 14) : isMobileXS ? 20 : 28,
                overflow: 'hidden',
                overflowX: 'auto',
                whiteSpace: 'nowrap',
              }}
              bold
            >
              <u>{hasTitle}</u>
            </Text>
          </div>
        )}

        {!state.open && getButton()}

        {state.open && (
          <AutoComplete
            autoFocus={!isMobileXS}
            variant="standard"
            getOptionLabel={(d) => {
              const name = get(d, 'name', '');
              return name;
            }}
            multiple={false}
            {...autoCompleteProps}
            options={props.options}
            ref={autoCompleteRef}
            placeholder={placeholder}
            onOpen={onOpen}
            onClose={onClose}
            onChange={onChange}
            open={state.open}
            alwaysInclude={[{ name: `+ Add new ${tierChildLabel}`, id: 'addnew' }]}
          />
        )}
      </BtnContainer>
      <NewRealmCreate {...createRealm} rootProps={props.rootProps} />
    </>
  );
};

const NewRealmCreate = (props) => {
  const { me } = useUser();
  const onSubmit = async (vals) => {
    try {
      const realms = get(props, 'rootProps.realms', {});
      const foundRealm = get(realms, 'data', []).find((r) => {
        return r.name.toLowerCase() === vals.name.toLowerCase();
      });

      if (foundRealm) {
        props.onSubmit(foundRealm);
      } else {
        const arealm = get(props, 'rootProps.assessment.data.realm');

        const tier = getRealmChildTier(get(arealm, 'tier', 'lot')).toLowerCase();

        const result = await sendData(
          {
            url: `/realms`,
            method: 'post',
            data: {
              data: {
                type: 'realms',
                attributes: {
                  user_id: me.id,
                  name: vals.name,
                  tier: tier,
                  parent_id: get(first(realms.data), 'parentId') || get(arealm, 'id'),
                },
              },
            },
          },
          { normalize: { build: ['realms'] } }
        );

        await get(props, 'rootProps.realms.update', () => 0)();

        props.onSubmit(result[0]);
      }
    } catch (err) {
      alert(err);
    }
  };

  return (
    <Modal {...props}>
      <Form
        showAllScreens
        onSubmit={onSubmit}
        submitText="Submit"
        canSubmit={true}
        submitBtnBoxStyle={{ position: 'relative' }}
        useCheckContinue
        screens={[
          {
            title: '',
            inputs: [
              {
                id: 'name',
                type: 'TextField',
                props: {
                  label: 'Name',
                  required: true,
                },
              },
            ],
          },
        ]}
      />
    </Modal>
  );
};

export const SubScoreLauncher = (props) => {
  const { pssvChecklist, pssvCheckpoint, pssvSystem, activeEdit, assessment, scorecard } = props;
  const [isOpen, setIsOpen] = useState(false);
  const isByLocation = get(props, 'filter.listGroup', '') === 'location';
  const [realm, setRealm] = useState(isByLocation && ['room', 'lot', 'community'].indexOf(get(pssvChecklist, 'type')) !== -1 ? pssvChecklist : {});
  const [pssvTag, setPssvTag] = useState(
    !!pssvCheckpoint
      ? {
          ...{
            pssvCheckpoint,
            pssvChecklist,
            pssvSystem,
          },
        }
      : null
  );
  const hasSubScores = get(props, 'scorecard.config.hasSubScores', true);

  const [filteredRealms, setFilteredRealms] = useState({
    data: [],
    loading: true,
  });

  const [alerts, setAlerts] = useState();

  useEffect(() => {
    setFilteredRealms({
      ...props.realms,
      data: filterRealmsByCps(get(props, 'realms.data'), scorecard, props),
    });
  }, [get(props, 'realms.data'), props.allScoreData]);

  const onCloseInstance = (created) => {
    if (created) {
      setAlerts({ message: 'INSTANCE ADDED!' });
    }
    if (!props.preserveRealmOnClose) {
      setRealm({});
    }
    setIsOpen(false);
  };

  const selectRealm = (e, realm) => {
    if (!realm) {
      return;
    }
    setRealm(realm);
  };

  const realmDrop = (p = {}) => {
    return (
      <ButtonType
        rootProps={props}
        onChange={(e, realm) => {
          selectRealm(e, realm);
          setIsOpen(true);
        }}
        options={get(filteredRealms, 'data', [])}
        {...p}
        {...get(props, 'realmDropProps', {})}
      />
    );
  };

  const clickAdd = () => {
    setIsOpen(true);
  };

  const renderButtonType = () => {
    if (size(realm) === 0) {
      return (
        <RoomBtnWrapper className="roomBtn">
          {realmDrop({
            variant: activeEdit ? 'outlined' : 'contained',
            size: 'small',
            placeholder: isByLocation ? 'ADD INSTANCE' : `ADD BY ${getRealmChildTier(get(props, 'assessment.data.realm.tier', 'lot'))}`,
            classes: { root: SUBS_ROOT_CLASS, input: SUBS_INPUT_CLASS },
          })}
        </RoomBtnWrapper>
      );
    }
    return (
      <Button style={{ marginLeft: 8, ...(props.style || {}) }} color="secondary" onClick={clickAdd} icon={'add'}>
        ADD INSTANCE
      </Button>
    );
  };

  return !hasSubScores ? null : (
    <>
      {renderButtonType()}
      {!!props.initialProcessedScore ? (
        // If there's an initialProcessedScore (pssvTag), open the SubScoreModal directly with the data.
        // If not, open the Realms Checkpoints list modal and allow the user to set pssvTag.
        (<SubScoreModal
          {...props}
          open={isOpen}
          isDialog={true}
          onClose={onCloseInstance}
          realm={realm}
          updateScores={props.updateScores}
          alerts={alerts}
          setAlerts={setAlerts}
          pssvTag={props.initialProcessedScore}
        />)
      ) : (
        <RealmsCheckpointsModal
          {...props}
          open={isOpen}
          setIsOpen={setIsOpen}
          realm={realm}
          setRealm={setRealm}
          updateScores={props.updateScores}
          alerts={alerts}
          setAlerts={setAlerts}
          realmDrop={realmDrop}
          filteredRealms={filteredRealms}
          assessment={assessment}
          scorecard={scorecard}
          pssvTag={pssvTag}
          setPssvTag={setPssvTag}
        />
      )}
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} />
    </>
  );
};
